import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import Button from "components/ui/Button";
import SearchSelect from "components/ui/SearchSelect";

const CATEGORY_OPTIONS = [
  { value: "media", label: "Media", description: "On-site contractor work" },
  {
    value: "postProduction",
    label: "Post Production",
    description: "Remote work, no calendar event",
  },
  { value: "print", label: "Print", description: "Physical product" },
  { value: "gifting", label: "Gifting", description: "Gifting product" },
  {
    value: "inspection",
    label: "Inspection",
    description: "Open homes / inspections",
  },
  {
    value: "meeting",
    label: "Meeting",
    description: "Key meetings, launch, notify",
  },
  {
    value: "milestone",
    label: "Milestone",
    description: "Closing, settlement, auction",
  },
];

const CONTRACTOR_SERVICES = [
  { value: "photo", label: "Photo" },
  { value: "video", label: "Video" },
  { value: "floorplan", label: "Floorplan" },
  { value: "floorplanRedraw", label: "Floorplan Redraw" },
  { value: "auction", label: "Auction" },
];

export default function EventTypeEdit({ initialData = {}, onSubmit }) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      key: initialData.key || "",
      name: initialData.name || "",
      category: initialData.category || "",
      requiresDuration:
        initialData.requiresDuration !== undefined
          ? initialData.requiresDuration
          : true,
      requiresContractor: initialData.requiresContractor || false,
      requiresAgent: initialData.requiresAgent || false,
      contractorService: initialData.contractorService || "",
      defaultDurationMinutes: initialData.defaultDurationMinutes || "",
      allowMultiple: initialData.allowMultiple || false,
      allowCustomTime: initialData.allowCustomTime || false,
      requiresPortalUpload: initialData.requiresPortalUpload || false,
      requiresCampaignUpdate: initialData.requiresCampaignUpdate || false,
      sizeOptions: initialData.sizeOptions?.join(", ") || "",
    },
  });

  // Register controlled fields with validation
  useEffect(() => {
    register("category", { required: "Category is required" });
    register("contractorService");
  }, [register]);

  const watchRequiresContractor = watch("requiresContractor");
  const watchRequiresDuration = watch("requiresDuration");
  const watchCategory = watch("category");
  const watchContractorService = watch("contractorService");

  const selectedCategory =
    CATEGORY_OPTIONS.find((opt) => opt.value === watchCategory) || null;
  const selectedContractorService =
    CONTRACTOR_SERVICES.find((opt) => opt.value === watchContractorService) ||
    null;

  const submitForm = (data) => {
    const transformedData = {
      ...data,
      defaultDurationMinutes:
        data.requiresDuration && data.defaultDurationMinutes
          ? parseInt(data.defaultDurationMinutes, 10)
          : null,
      contractorService: data.requiresContractor
        ? data.contractorService || null
        : null,
      sizeOptions: data.sizeOptions
        ? data.sizeOptions
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };
    onSubmit(transformedData);
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} className="space-y-6">
      {/* Key */}
      <div className="space-y-2">
        <label htmlFor="key" className="form-label">
          Key *
        </label>
        <input
          type="text"
          id="key"
          {...register("key", {
            required: "Key is required",
            pattern: {
              value: /^[a-zA-Z][a-zA-Z0-9]*$/,
              message: "Key must be alphanumeric and start with a letter",
            },
          })}
          className={`form-input border ${errors.key ? "border-red-500" : "border-mediumgray"}`}
          placeholder="e.g., openHome, photo, virtualFurniture"
          disabled={!!initialData._id}
        />
        {errors.key && (
          <p className="form-error-message">{errors.key.message}</p>
        )}
        {initialData._id && (
          <p className="text-sm text-darkgray">
            Key cannot be changed after creation.
          </p>
        )}
      </div>

      {/* Event Name */}
      <div className="space-y-2">
        <label htmlFor="name" className="form-label">
          Event Name *
        </label>
        <input
          type="text"
          id="name"
          {...register("name", { required: "Event name is required" })}
          className={`form-input border ${errors.name ? "border-red-500" : "border-mediumgray"}`}
          placeholder="e.g., Open Home, Photography, Virtual Furniture"
        />
        {errors.name && (
          <p className="form-error-message">{errors.name.message}</p>
        )}
      </div>

      {/* Category */}
      <div className="space-y-2">
        <label htmlFor="category" className="form-label">
          Category *
        </label>
        <SearchSelect
          options={CATEGORY_OPTIONS.map((opt) => ({
            value: opt.value,
            label: `${opt.label} — ${opt.description}`,
          }))}
          value={
            selectedCategory
              ? {
                  value: selectedCategory.value,
                  label: `${selectedCategory.label} — ${selectedCategory.description}`,
                }
              : null
          }
          onChange={(opt) =>
            setValue("category", opt?.value || "", {
              shouldValidate: true,
            })
          }
          placeholder="Select a category"
          isClearable
        />
        {errors.category && (
          <p className="form-error-message">{errors.category.message}</p>
        )}
      </div>

      {/* Size Options */}
      <div className="space-y-2">
        <label htmlFor="sizeOptions" className="form-label">
          Size Options
        </label>
        <input
          type="text"
          id="sizeOptions"
          {...register("sizeOptions")}
          className="form-input border border-mediumgray"
          placeholder="e.g., small, medium, large, extraLarge"
        />
        <p className="text-sm text-darkgray">
          Comma-separated list of valid sizes for this event type. Leave empty
          if no size variants.
        </p>
      </div>

      {/* Requires Duration */}
      <div className="space-y-2">
        <label className="form-label">Duration Settings</label>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="requiresDuration"
            {...register("requiresDuration")}
            className="w-4 h-4"
          />
          <label htmlFor="requiresDuration" className="text-sm">
            This event type requires a duration when creating campaign events
          </label>
        </div>
      </div>

      {/* Default Duration — only shown when requiresDuration is checked */}
      {watchRequiresDuration && (
        <div className="space-y-2">
          <label htmlFor="defaultDurationMinutes" className="form-label">
            Default Duration (minutes)
          </label>
          <input
            type="number"
            id="defaultDurationMinutes"
            min="5"
            max="1440"
            step="5"
            {...register("defaultDurationMinutes", {
              min: { value: 5, message: "Minimum 5 minutes" },
              max: {
                value: 1440,
                message: "Maximum 1440 minutes (24 hours)",
              },
            })}
            className={`form-input border ${errors.defaultDurationMinutes ? "border-red-500" : "border-mediumgray"}`}
            placeholder="e.g., 30, 60, 90"
          />
          {errors.defaultDurationMinutes && (
            <p className="form-error-message">
              {errors.defaultDurationMinutes.message}
            </p>
          )}
          <p className="text-sm text-darkgray">
            Leave empty if duration varies per event.
          </p>
        </div>
      )}

      {/* Requires Contractor */}
      <div className="space-y-2">
        <label className="form-label">Contractor Requirements</label>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="requiresContractor"
            {...register("requiresContractor")}
            className="w-4 h-4"
          />
          <label htmlFor="requiresContractor" className="text-sm">
            This event requires a contractor
          </label>
        </div>
      </div>

      {/* Contractor Service */}
      {watchRequiresContractor && (
        <div className="space-y-2">
          <label htmlFor="contractorService" className="form-label">
            Contractor Service
          </label>
          <SearchSelect
            options={CONTRACTOR_SERVICES}
            value={selectedContractorService}
            onChange={(opt) => setValue("contractorService", opt?.value || "")}
            placeholder="Select a service"
            isClearable
          />
          <p className="text-sm text-darkgray">
            The type of contractor service needed for this event.
          </p>
        </div>
      )}

      {/* Agent Requirement */}
      <div className="space-y-2">
        <label className="form-label">Agent Requirement</label>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="requiresAgent"
            {...register("requiresAgent")}
            className="w-4 h-4"
          />
          <label htmlFor="requiresAgent" className="text-sm">
            This event requires an agent (checks agent's Google Calendar for
            conflicts)
          </label>
        </div>
      </div>

      {/* Scheduling Options */}
      <div className="space-y-2">
        <label className="form-label">Scheduling Options</label>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="allowMultiple"
              {...register("allowMultiple")}
              className="w-4 h-4"
            />
            <label htmlFor="allowMultiple" className="text-sm">
              Allow multiple events per property
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="allowCustomTime"
              {...register("allowCustomTime")}
              className="w-4 h-4"
            />
            <label htmlFor="allowCustomTime" className="text-sm">
              Allow custom time (duration picker instead of fixed slots)
            </label>
          </div>
        </div>
      </div>

      {/* Flags */}
      <div className="space-y-2">
        <label className="form-label">Additional Flags</label>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="requiresPortalUpload"
              {...register("requiresPortalUpload")}
              className="w-4 h-4"
            />
            <label htmlFor="requiresPortalUpload" className="text-sm">
              Requires portal upload
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="requiresCampaignUpdate"
              {...register("requiresCampaignUpdate")}
              className="w-4 h-4"
            />
            <label htmlFor="requiresCampaignUpdate" className="text-sm">
              Requires campaign update
            </label>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end mt-6 pt-4 border-t border-mediumgray">
        <Button type="submit" className="black-button">
          {initialData._id ? "Update Event Type" : "Create Event Type"}
        </Button>
      </div>
    </form>
  );
}
